<template>
  <div>
    <!-- Reflection View/Edit Modal -->
    <modal
      class="reflection-modal"
      :name="'reflection-modal' + (post && post.id ? post.id : '')"
      :width="modalWidth"
      height="auto"
      :clickToClose="false"
    >
      <v-container class="reflection-form">
        <div class="header header-row">
          <h2 class="header-title">
            {{ $t('general.reflection.modal-title') }}
          </h2>
          <Button
            v-if="userHasTranslation && !isReflectionAuthor"
            class="translate-button"
            @click.native="toggleTranslation"
            :disabled="translationLoading"
            title
            shadow="true"
            color="#00A9B3"
            :bgcolor="showTranslation ? '#F3BDBF' : '#D9F0EA'"
            height="44"
            width="44px"
            margin="0px"
            padding="0px"
            icon="translate"
          />
        </div>

        <div class="error" v-if="reflectionError">
          <p>{{ reflectionError }}</p>
        </div>

        <div v-if="reflectionLoading" style="padding: 12px; background-color: #e3f2fd; border-radius: 4px; color: #1565c0; margin-bottom: 16px;">
          {{ $t('general.reflection.loading') }}
        </div>

        <div v-if="reflection">
          <!-- Visibility checkboxes -->
          <div v-if="isReflectionAuthor" class="visibility-section" style="margin-bottom: 24px; padding: 16px; border: 1px solid #e0e0e0; border-radius: 4px;">
            <p style="color: #00A9B3; margin-bottom: 12px; font-weight: 500">
              {{ $t('general.reflection.visibility-title') }}
            </p>
            
            <!-- Only myself checkbox (disabled - always checked) -->
            <label class="checkbox-label" style="display: block; margin-bottom: 8px;">
              <input
                type="checkbox"
                v-model="reflection.isVisibleToSelf"
                disabled
                style="accent-color: #00A9B3"
              />
              <span style="margin-left: 8px">{{ $t('general.reflection.visibility-self') }}</span>
            </label>

            <!-- Parents checkbox -->
            <label class="checkbox-label" style="display: block; margin-bottom: 8px;">
              <input
                type="checkbox"
                v-model="reflection.isVisibleToParents"
                :disabled="!isReflectionAuthor"
                style="accent-color: #00A9B3"
              />
              <span style="margin-left: 8px">{{ $t('general.reflection.visibility-parents') }}</span>
            </label>

            <!-- Hospital section checkbox -->
            <label class="checkbox-label" style="display: block; margin-bottom: 8px;">
              <input
                type="checkbox"
                v-model="reflection.isVisibleToHospital"
                :disabled="!isReflectionAuthor"
                style="accent-color: #00A9B3"
              />
              <span style="margin-left: 8px">{{ $t('general.reflection.visibility-hospital') }}</span>
            </label>
          </div>

          <!-- Content -->
          <div style="margin-bottom: 24px;">
            <label :for="reflectionContentId" style="display: block; color: #00A9B3; margin: 0 0 8px 0; font-weight: 500;">
              {{ $t('general.reflection.form-content') }}
            </label>
            <textarea
              v-if="isReflectionAuthor"
              id="reflection-content-edit"
              v-model="reflection.content"
              rows="16"
              style="width: 100%; border: 1px solid #ddd; padding: 8px; border-radius: 4px;"
            ></textarea>
            <textarea
              v-else
              id="reflection-content-read"
              :value="displayedContent"
              readonly
              rows="16"
              style="width: 100%; border: 1px solid #e0e0e0; padding: 8px; border-radius: 4px; background-color: #fafafa; resize: none; overflow-y: auto; line-height: 1.5; font-family: inherit; font-size: inherit;"
            ></textarea>
          </div>

          <!-- Action buttons -->
          <div class="button-group" style="display: flex; gap: 12px; justify-content: center; margin-top: 24px;">
            <Button
              @click.native="closeReflectionModal"
              :title="$t('general.reflection.action-close')"
              color="#e05484"
              bgcolor="#fff"
              style="border: 2px solid #e05484"
            />
            <Button
              v-if="isReflectionAuthor"
              @click.native="saveReflection"
              :title="$t('general.reflection.action-save')"
              bgcolor="#e05484"
              color="#fff"
            />
          </div>
        </div>
      </v-container>
    </modal>
  </div>
</template>

<script>
import { neoparentReflections } from '@/api';
import Button from '@/page_elements/Button';

export default {
  name: "Reflection",
  components: {
    Button
  },
  props: {
    post: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      reflection: null,
      reflectionLoading: false,
      reflectionError: "",
      showTranslation: false,
      translation: null,
      translationLoading: false,
      isMobileViewport: window.innerWidth <= 768
    };
  },
  computed: {
    modalWidth() {
      return this.isMobileViewport ? "92%" : "70%";
    },
    reflectionContentId() {
      return this.isReflectionAuthor ? 'reflection-content-edit' : 'reflection-content-read';
    },
    reflectionId() {
      if (!this.post) {
        return null;
      }
      return this.post.reflection ? this.post.reflection.id : (this.post.Reflection ? this.post.Reflection.id : null);
    },
    isReflectionAuthor() {
      return this.reflection && this.reflection.userId === this.$store.getters.user.id;
    },
    userHasTranslation() {
      const user = this.$store.getters.user;
      const hasDeepl = !!(user && user.hospital && user.hospital.hasDeepl);

      if (!hasDeepl || !user) {
        return false;
      }

      if (user.role !== 'p') {
        return true;
      }

      return !!user.translationLanguage;
    },
    displayedContent() {
      if (!this.reflection) {
        return "";
      }

      if (!this.showTranslation || !this.translation) {
        return this.reflection.content;
      }

      return this.translation;
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateViewportMode);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewportMode);
  },
  methods: {
    updateViewportMode() {
      this.isMobileViewport = window.innerWidth <= 768;
    },
    async openReflectionModal() {
      this.reflectionError = "";
      this.showTranslation = false;
      this.translation = null;
      this.translationLoading = false;
      await this.loadReflection();
      this.$modal.show('reflection-modal' + this.post.id);
    },
    closeReflectionModal() {
      this.$modal.hide('reflection-modal' + this.post.id);
    },
    async loadReflection() {
      if (!this.reflectionId) {
        return;
      }
      if (this.reflection) {
        return;
      }

      this.reflectionLoading = true;
      try {
        const res = await neoparentReflections.getReflection(this.reflectionId);
        // Keep a local copy — do NOT share the reference with post.reflection,
        // otherwise v-model on the textarea would mutate the feed preview live.
        this.reflection = { ...res.data.reflection };
      } catch (err) {
        this.reflectionError = this.$t('general.reflection.error-load');
      } finally {
        this.reflectionLoading = false;
      }
    },
    async saveReflection() {
      if (!this.reflection || !this.isReflectionAuthor) {
        return;
      }
      if (!this.reflection.content) {
        this.reflectionError = this.$t('general.reflection.error-incomplete');
        return;
      }

      this.reflectionLoading = true;
      try {
        const res = await neoparentReflections.update(this.reflection.id, {
          content: this.reflection.content,
          isVisibleToSelf: true,
          isVisibleToParents: this.reflection.isVisibleToParents,
          isVisibleToHospital: this.reflection.isVisibleToHospital
        });
        // Update the local reflection object with the response data
        if (res.data.reflection) {
          this.reflection = res.data.reflection;
        }
        // Use $set so Vue 2 tracks the change even when the property was initially null
        const reflectionKey = this.post.Reflection !== undefined ? 'Reflection' : 'reflection';
        this.$set(this.post, reflectionKey, this.reflection);
        this.reflectionError = "";
        
        // Emit event to notify parent that reflection was updated
        this.$emit('reflection-updated', this.reflection);
        
        // Close modal after successful save
        this.closeReflectionModal();
      } catch (err) {
        this.reflectionError = this.$t('general.reflection.error-save-failed');
      } finally {
        this.reflectionLoading = false;
      }
    },
    async toggleTranslation() {
      if (this.isReflectionAuthor || !this.reflectionId) {
        return;
      }

      this.showTranslation = !this.showTranslation;

      if (this.translation || !this.showTranslation) {
        return;
      }

      this.translationLoading = true;
      try {
        const res = await neoparentReflections.translate(this.reflectionId);
        this.translation = res.data.translation || "";
      } catch (err) {
        this.showTranslation = false;
        this.reflectionError = this.$t('general.reflection.error-load');
      } finally {
        this.translationLoading = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.reflection-modal {
  .reflection-form {
    padding: 24px;
    width: 100%;
    margin: 0 auto;

    .header {
      margin-bottom: 20px;
    }

    .header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .header-title {
      color: #00A9B3;
      margin: 0;
    }

    .error {
      background-color: #ffebee;
      border-left: 4px solid #f44336;
      padding: 12px 16px;
      border-radius: 4px;
      margin-bottom: 16px;

      p {
        margin: 0;
        color: #c62828;
      }
    }

    .visibility-section {
      .checkbox-label {
        cursor: pointer;
        user-select: none;

        input {
          cursor: pointer;
        }

        input:disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }
      }
    }

    .button-group {
      margin-top: 20px;
    }
  }
}

@media (max-width: 700px) {
  .reflection-modal {
    .reflection-form {
      max-width: 100%;
      padding: 14px;

      .visibility-section {
        margin-bottom: 16px !important;
        padding: 12px !important;
      }

      textarea {
        font-size: 16px;
        min-height: 240px;
      }

      .button-group {
        flex-wrap: wrap;

        > * {
          width: 100%;
        }
      }
    }
  }
}
</style>
